import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Typography,
  Button,
  Tooltip,
  Box,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { makeStyles } from "tss-react/mui";

import TableRowSkeleton from "../../components/TableRowSkeleton";
import api from "../../services/api";
import { toast } from "../../services/toast";

const STATUS_STYLE = {
  open: { label: "Atendiendo", bg: "#2e7d32" },
  pending: { label: "Sin atender", bg: "#ed6c02" },
  closed: { label: "Cerrado", bg: "#616161" },
  bot: { label: "Bot", bg: "#0288d1" },
};

const useStyles = makeStyles()((theme) => ({
  filters: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
    padding: theme.spacing(1.5, 1, 1),
    "& .MuiSelect-select": {
      paddingTop: 7,
      paddingBottom: 7,
      fontSize: 13.5,
    },
  },
  table: {
    minWidth: 960,
    "& th": {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      background: "transparent",
      whiteSpace: "nowrap",
    },
    "& td": {
      fontSize: 13.5,
    },
    "& tr:last-of-type td": {
      borderBottom: "none",
    },
  },
  row: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "light"
          ? "rgba(0,0,0,0.03)"
          : "rgba(255,255,255,0.04)",
    },
  },
  contactName: {
    fontWeight: 600,
    lineHeight: 1.2,
  },
  contactNumber: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  muted: {
    color: theme.palette.text.disabled,
  },
  motivo: {
    fontWeight: 600,
  },
  motivoPor: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  contador: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "baseline",
    gap: 6,
  },
  contadorNumero: {
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1,
    color: theme.palette.text.primary,
  },
  contadorTexto: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
}));

const TicketsReport = ({ campaignOptions, platform, days, closureOptions = [], category = "", onCategoryChange }) => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ tickets: [], count: 0, hasMore: false });
  const [pageNumber, setPageNumber] = useState(1);

  const [campaign, setCampaign] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/ticket/ad-campaigns/tickets", {
        params: {
          campaign: campaign || undefined,
          status: status || undefined,
          days: days || undefined,
          platform,
          category: category || undefined,
          pageNumber,
        },
      });
      setData(res);
    } catch (e) {
      toast.error(e);
    } finally {
      setLoading(false);
    }
  }, [campaign, status, days, platform, category, pageNumber]);

  useEffect(() => {
    setCampaign("");
  }, [platform]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPageNumber(1);
  }, [campaign, status, days, platform, category]);

  const [descargando, setDescargando] = useState(false);

  const descargar = async () => {
    setDescargando(true);
    try {
      const { data: blob } = await api.get("/ticket/ad-campaigns/tickets/export", {
        responseType: "blob",
        params: {
          campaign: campaign || undefined,
          status: status || undefined,
          days: days || undefined,
          platform,
          category: category || undefined,
        },
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `anuncios-tickets-${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo descargar el reporte.");
    } finally {
      setDescargando(false);
    }
  };

  const fecha = (v) =>
    v
      ? new Date(v).toLocaleString("es-PE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  return (
    <>
      <div className={classes.filters}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <Select
            displayEmpty
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            renderValue={(v) => {
              if (!v) return "Todas las campañas";
              const o = campaignOptions.find((c) => c.adReferralSourceId === v);
              return o?.campaignName || o?.adReferralTitle || "Campaña";
            }}
          >
            <MenuItem value="">Todas las campañas</MenuItem>
            {campaignOptions.map((c) => (
              <MenuItem key={c.adReferralSourceId} value={c.adReferralSourceId}>
                {c.campaignName || c.adReferralTitle || c.adReferralSourceId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <Select
            displayEmpty
            value={category}
            onChange={(e) => onCategoryChange && onCategoryChange(e.target.value)}
            renderValue={(v) => {
              if (!v) return "Todos los ámbitos";
              return closureOptions.find((c) => c.key === v)?.name || "Motivo";
            }}
          >
            <MenuItem value="">Todos los ámbitos</MenuItem>
            {closureOptions.map((c) => (
              <MenuItem key={c.key} value={c.key}>
                <span
                  style={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    marginRight: 8,
                    backgroundColor: c.color,
                  }}
                />
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            displayEmpty
            value={category ? "closed" : status}
            disabled={!!category}
            onChange={(e) => setStatus(e.target.value)}
            renderValue={(v) => STATUS_STYLE[v]?.label || "Todos los estados"}
          >
            <MenuItem value="">Todos los estados</MenuItem>
            <MenuItem value="pending">Sin atender</MenuItem>
            <MenuItem value="open">Atendiendo</MenuItem>
            <MenuItem value="closed">Cerrado</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          disabled={descargando || !data.count}
          onClick={descargar}
          sx={{ height: 36, textTransform: "none", fontWeight: 600 }}
        >
          {descargando ? "Generando…" : "Excel"}
        </Button>

        <Tooltip title="Cada fila es una entrada desde un anuncio. Un contacto que llegó por dos campañas aparece en las dos.">
          <div className={classes.contador}>
            <span className={classes.contadorNumero}>{data.count}</span>
            <span className={classes.contadorTexto}>
              llegada{data.count === 1 ? "" : "s"}
            </span>
          </div>
        </Tooltip>
      </div>

      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell align="left">Contacto</TableCell>
            <TableCell align="left">Campaña</TableCell>
            <TableCell align="center">Estado</TableCell>
            <TableCell align="left">Atendiendo</TableCell>
            <TableCell align="left">Cerrado</TableCell>
            <TableCell align="left">Motivo de cierre</TableCell>
            <TableCell align="left">Departamento</TableCell>
            <TableCell align="center">Últ. actividad</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRowSkeleton columns={8} />
          ) : data.tickets?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                  No hay tickets con esos filtros.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.tickets.map((t) => {
              const st = STATUS_STYLE[t.status] || {
                label: t.status,
                bg: "#9e9e9e",
              };
              const cerrado = t.status === "closed";

              return (
                <TableRow
                  key={t.id}
                  className={classes.row}
                  onClick={() => navigate(`/tickets/${t.uuid}`)}
                >
                  <TableCell align="left">
                    <div className={classes.contactName}>
                      {t.contact?.name || "Sin nombre"}
                    </div>
                    <div className={classes.contactNumber}>
                      {t.contact?.number}
                    </div>
                  </TableCell>

                  <TableCell align="left">
                    {t.campaign || "—"}
                    {t.otrosAnuncios > 0 && (
                      <Tooltip
                        title={`Este contacto también llegó por ${
                          t.otrosAnuncios
                        } anuncio${t.otrosAnuncios === 1 ? "" : "s"} más. Aparece
                          una vez por cada campaña que lo trajo.`}
                      >
                        <Chip
                          label={`+${t.otrosAnuncios}`}
                          size="small"
                          sx={{ height: 18, fontSize: 11, ml: 0.75 }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Chip
                      label={st.label}
                      size="small"
                      sx={{
                        height: 22,
                        fontWeight: 600,
                        color: "#fff",
                        backgroundColor: st.bg,
                      }}
                    />
                  </TableCell>

                  {}
                  <TableCell align="left">
                    {t.user || (
                      <span className={classes.muted}>
                        {cerrado ? "—" : "Sin asignar"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell align="left">
                    {t.userLast || <span className={classes.muted}>—</span>}
                  </TableCell>

                  <TableCell align="left">
                    {cerrado ? (
                      (() => {
                        const opcion = closureOptions.find((c) => c.key === t.closeCategory);
                        const razon = t.closeReason || t.motivo;
                        return (
                          <>
                            <div className={classes.motivo} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              {opcion && (
                                <span
                                  style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    flexShrink: 0,
                                    backgroundColor: opcion.color,
                                  }}
                                />
                              )}
                              {razon || "Sin motivo"}
                            </div>
                            {(opcion?.name || t.closedBy || t.motivoPor) && (
                              <div className={classes.motivoPor}>
                                {[opcion && razon ? opcion.name : null, t.closedBy || t.motivoPor ? `por ${t.closedBy || t.motivoPor}` : null]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </div>
                            )}
                          </>
                        );
                      })()
                    ) : (
                      <span className={classes.muted}>—</span>
                    )}
                  </TableCell>

                  <TableCell align="left">
                    {t.queue ? (
                      <span style={{ color: t.queue.color }}>
                        {t.queue.name}
                      </span>
                    ) : (
                      <span className={classes.muted}>Sin departamento</span>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                      {fecha(t.updatedAt)}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {(pageNumber > 1 || data.hasMore) && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
          <Button
            size="small"
            disabled={pageNumber === 1 || loading}
            onClick={() => setPageNumber((p) => p - 1)}
          >
            Anterior
          </Button>
          <Button
            size="small"
            disabled={!data.hasMore || loading}
            onClick={() => setPageNumber((p) => p + 1)}
          >
            Siguiente
          </Button>
        </Box>
      )}
    </>
  );
};

export default TicketsReport;
